import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCalculator, FaChevronLeft, FaChevronRight, FaAngleUp, FaAngleDown } from 'react-icons/fa';
import ConversionExplanationDecimalToAll from './ConversionExplanationDecimalToAll';
import ConversionFlow from '../../ConversionFlow';

import { specialConversions } from '../../../../utils/unitConversions/number/numberUtils';
const ConversionExplanationOctalToAll = ({ conversionType, fromUnit, toUnit, inputValue, result, darkMode, isEncodingMode, onEncodingModeToggle }) => {  const [showIntermediateConversion, setShowIntermediateConversion] = useState(false);
  const [showDetailedStep2, setShowDetailedStep2] = useState(true);
  const [showDetailedConversion, setShowDetailedConversion] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswer, setQuizAnswer] = useState('');
  const [quizResult, setQuizResult] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isConversionRendered, setIsConversionRendered] = useState(false);
  const [selectedChar, setSelectedChar] = useState('');

  const [showScientificNotationQuiz, setShowScientificNotationQuiz] = useState(false);
  const [scientificNotationQuizAnswer, setScientificNotationQuizAnswer] = useState('');
  const [scientificNotationQuizResult, setScientificNotationQuizResult] = useState(null);

  const handleQuizSubmit = useCallback(() => {
    const isCorrect = quizAnswer.toLowerCase() === result.toLowerCase();
    setQuizResult(isCorrect);
  }, [quizAnswer, result]);

  const renderExampleOrConversionText = useCallback((octalValue, isExample) => (
    isExample ? (
      <p className="mb-4 text-yellow-600 italic">
        Example: Let's convert the octal number {octalValue} to {toUnit}.
      </p>
    ) : (
      <p className="mb-4 text-green-600">
        Your conversion: {octalValue} (Octal) to {toUnit}
      </p>
    )
  ), [toUnit]);

  const handleScientificNotationQuizSubmit = useCallback(() => {
    const correctAnswer = result ? parseFloat(result).toExponential() : '';
    const isCorrect = scientificNotationQuizAnswer.trim().toLowerCase() === correctAnswer.trim().toLowerCase();
    setScientificNotationQuizResult(isCorrect);
  }, [scientificNotationQuizAnswer, result]);

  const toggleIntermediateConversion = () => setShowIntermediateConversion(!showIntermediateConversion);
  const toggleDetailedConversion = () => setShowDetailedConversion(!showDetailedConversion);
  const toggleDetailedStep2 = () => setShowDetailedStep2(!showDetailedStep2);

  const getIntermediateConversionExplanation = useCallback((octalValue) => {
    const decimalValue = parseInt(octalValue, 8);
    const steps = octalValue.split('').reverse();

    const renderOctalToDecimalConversion = () => (
      <div className="relative mt-12 flex flex-col items-center">
        <div className="absolute top-0 left-0 text-2xl font-bold main-number bg-gray-100 dark:bg-gray-700 p-2 rounded-md shadow-md">
          {octalValue.split('').map((digit, index) => (
            <span key={index} className={`transition-colors duration-300 ${steps.length - 1 - index === currentStep ? 'text-blue-500 dark:text-blue-400' : 'dark:text-white'}`}>
              {digit}
            </span>
          ))}
          <sub className="dark:text-white">8</sub>
        </div>
        <div className="flex flex-col items-center space-y-4 mt-16">
          {steps.map((digit, index) => {
            const value = parseInt(digit) * Math.pow(8, index);
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center space-x-4 p-4 rounded-lg shadow-md ${isActive ? 'bg-blue-50 dark:bg-blue-900' :
                    isCompleted ? 'text-gray-500 dark:text-gray-300 bg-gray-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-700'
                  } transition-all duration-300`}
              >
                <div className={`w-12 text-right digit ${isActive ? 'text-blue-500 dark:text-blue-400 font-bold' : 'dark:text-white'} transition-colors duration-300`}>{digit}</div>
                <div className="w-8 text-center dark:text-white">×</div>
                <div className="w-16 text-center group relative dark:text-white">
                  8<sup>{index}</sup>
                  <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-800 text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    8{index > 0 ? <sup>{index}</sup> : ''} = {Math.pow(8, index)}
                  </span>
                </div>
                <div className="w-8 text-center dark:text-white">=</div>
                <div className={`w-20 text-left calculation ${isActive ? 'text-blue-500 dark:text-blue-400 font-bold' : 'dark:text-white'} transition-colors duration-300`}>{value}</div>
              </motion.div>
            );
          })}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: currentStep === steps.length ? 1 : 0, y: currentStep === steps.length ? 0 : 10 }}
            className="font-bold text-xl mt-6 pt-4 border-t border-gray-300 dark:border-gray-600 dark:text-white"
          >
            Sum: {decimalValue} (Decimal)
          </motion.div>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        {showIntermediateConversion && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className={`mt-6 p-6 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg shadow-lg relative overflow-hidden`}
            style={{
              background: darkMode
                ? 'linear-gradient(145deg, #2d3748, #1a202c)'
                : 'linear-gradient(145deg, #f7fafc, #edf2f7)'
            }}
          >
            <h4 className="text-2xl font-semibold mb-4 flex items-center">
              <FaCalculator className="mr-2" />
              Octal to Decimal Conversion
            </h4>
            <p className="mb-6 leading-relaxed">
              To convert octal to decimal, we multiply each digit by 8 raised to the power of its position (starting from 0) and sum the results.
            </p>

            {renderOctalToDecimalConversion()}

            <div className="mt-8 flex justify-between">
              <button
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                className={`px-4 py-2 flex items-center ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                  } text-white rounded-md transition-all duration-300 transform hover:scale-105`}
                disabled={currentStep === 0}
              >
                <FaChevronLeft className="mr-2" /> Previous
              </button>
              <button
                onClick={() => setCurrentStep(Math.min(steps.length, currentStep + 1))}
                className={`px-4 py-2 flex items-center ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                  } text-white rounded-md transition-all duration-300 transform hover:scale-105`}
                disabled={currentStep === steps.length}
              >
                Next <FaChevronRight className="ml-2" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }, [showIntermediateConversion, darkMode, currentStep]);

  const getOctalToDecimalExplanation = useCallback((octalValue, isExample, exampleResult) => {
    const decimalValue = parseInt(octalValue, 8);

    const steps = octalValue.split('').reverse().map((digit, index) => ({
      digit: parseInt(digit),
      power: index,
      value: parseInt(digit) * Math.pow(8, index)
    }));

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Octal to Decimal Conversion Works</h3>
        {renderExampleOrConversionText(octalValue, isExample)}
        <p className="mb-4">
          To convert octal to decimal, we multiply each digit by 8 raised to the power of its position (starting from 0) and sum the results.
        </p>
        <div className="overflow-x-auto">
          <table className={`w-full text-left ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <thead>
              <tr>
                <th className="px-4 py-2">Digit</th>
                <th className="px-4 py-2">Position</th>
                <th className="px-4 py-2">Calculation</th>
                <th className="px-4 py-2">Value</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((step, index) => (
                <tr
                  key={index}
                  className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}
                >
                  <td className="border px-4 py-2">{step.digit}</td>
                  <td className="border px-4 py-2">{step.power}</td>
                  <td className="border px-4 py-2">{step.digit} × 8<sup>{step.power}</sup></td>
                  <td className="border px-4 py-2">{step.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4">
          Sum of all values: <span className="font-bold text-purple-500">{decimalValue}</span> (Decimal)
        </p>
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{octalValue}</span> (Octal) = <span className="text-purple-500">{isExample ? exampleResult : decimalValue}</span> (Decimal)
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText]);

  const getOctalToOtherBaseExplanation = useCallback((octalValue, isExample, base, baseLabel, exampleResult) => {
    const decimalValue = parseInt(octalValue, 8);
    const convertedValue = isExample ? exampleResult : result;

    const CompactExplanation = () => (
      <div className="mt-4 p-6 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900 dark:to-green-900 rounded-lg shadow-md">
        <p className="text-center font-bold text-2xl mb-4">
          <span className="text-blue-600 dark:text-blue-400">{octalValue}</span><sub className="text-black dark:text-white">8</sub> →
          <span className="text-purple-600 dark:text-purple-400">{decimalValue}</span><sub className="text-black dark:text-white">10</sub> →
          <span className="text-green-600 dark:text-green-400">{convertedValue}</span><sub className="text-black dark:text-white">{base}</sub>
        </p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="font-semibold text-blue-600 dark:text-blue-400">Octal</p>
            <p className="text-lg">{octalValue}</p>
          </div>
          <div>
            <p className="font-semibold text-purple-600 dark:text-purple-400">Decimal</p>
            <p className="text-lg">{decimalValue}</p>
          </div>
          <div>
            <p className="font-semibold text-green-600 dark:text-green-400">{baseLabel}</p>
            <p className="text-lg">{convertedValue}</p>
          </div>
        </div>
        <p className="mt-4 text-center text-gray-600 dark:text-gray-300 italic">
          Click "Show Details" for step-by-step explanation
        </p>
      </div>
    );

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Octal to {baseLabel} Conversion Works</h3>
        {renderExampleOrConversionText(octalValue, isExample)}
        <p className="mb-4">
          To convert an octal number to {baseLabel}, we first convert the octal number to decimal, and then we convert the decimal to {baseLabel}.
        </p>
        <ConversionFlow
          steps={['Octal', 'Decimal', baseLabel]}
          darkMode={darkMode}
        />
        <ol className="list-decimal list-inside mb-4">
          <li className="mb-2">
            Convert octal to decimal: {octalValue}<sub>8</sub> = {decimalValue}<sub>10</sub>
            <button
              onClick={toggleIntermediateConversion}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showIntermediateConversion ? 'Hide Details' : 'Show Details'}
            </button>
          </li>
          {getIntermediateConversionExplanation(octalValue)}
          <li className="mb-2">
            Now, we convert the decimal number <span className="font-bold text-blue-500">{decimalValue}</span> to {baseLabel}:
            <button
              onClick={toggleDetailedStep2}
              className="ml-2 px-3 py-1.5 text-sm rounded-md transition-all duration-200
                       flex items-center gap-2 group
                       bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700
                       text-white shadow-sm hover:shadow
                       dark:from-blue-600 dark:to-blue-700 dark:hover:from-blue-700 dark:hover:to-blue-800"
            >
              {showDetailedStep2 ? (
                <>
                  <span>Hide Details</span>
                  <FaAngleUp className="w-4 h-4 transform group-hover:-translate-y-0.5 transition-transform" />
                </>
              ) : (
                <>
                  <span>Show Details</span>
                  <FaAngleDown className="w-4 h-4 transform group-hover:translate-y-0.5 transition-transform" />
                </>
              )}
            </button>
            {showDetailedStep2 ? (
              <>
                <p className="mt-2 text-center font-semibold">
                  <span className="text-blue-500">{octalValue}</span><sub className="text-black">8</sub> →
                  <span className="text-blue-500">{decimalValue}</span><sub className="text-black">10</sub> →
                  <span className="text-blue-500">...</span>
                  <sub className="text-black">{base}</sub>
                </p>
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-4 rounded-xl overflow-hidden
                             border border-gray-200/80 dark:border-gray-700/80
                             bg-gradient-to-br from-gray-50 to-white
                             dark:from-gray-800/50 dark:to-gray-800
                             shadow-sm backdrop-blur-sm"
                  >
                    <div className="p-6">
                      <ConversionExplanationDecimalToAll
                        conversionType="number"
                        fromUnit="Decimal"
                        toUnit={baseLabel}
                        inputValue={decimalValue.toString()}
                        result={convertedValue}
                        darkMode={darkMode}
                      />
                    </div>
                  </motion.div>
                </AnimatePresence>
              </>
            ) : (
              <CompactExplanation />
            )}
          </li>
        </ol>
        {showDetailedStep2 && (
          <>
            <p className="mt-4 text-center font-semibold text-lg">
              Complete conversion flow:
            </p>
            <div className="mt-4 flex items-center justify-center gap-3">
              <div className="group relative">
                <span className="font-bold text-blue-500 text-lg">{octalValue}
                  <sub className="text-gray-600 dark:text-gray-400">8</sub>
                </span>
                <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 
                                text-xs text-gray-500 dark:text-gray-400 opacity-0 
                                group-hover:opacity-100 transition-opacity">
                  Octal
                </div>
              </div>
              
              <div className="flex items-center text-gray-400 dark:text-gray-500">
                <div className="w-12 h-[2px] bg-gradient-to-r from-blue-500 to-purple-500"></div>
              </div>
              
              <div className="group relative">
                <span className="font-bold text-purple-500 text-lg">{decimalValue}
                  <sub className="text-gray-600 dark:text-gray-400">10</sub>
                </span>
                <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 
                                text-xs text-gray-500 dark:text-gray-400 opacity-0 
                                group-hover:opacity-100 transition-opacity">
                  Decimal
                </div>
              </div>
              
              <div className="flex items-center text-gray-400 dark:text-gray-500">
                <div className="w-12 h-[2px] bg-gradient-to-r from-purple-500 to-green-500"></div>
              </div>
              
              <div className="group relative">
                <span className="font-bold text-green-500 text-lg">{convertedValue}
                  <sub className="text-gray-600 dark:text-gray-400">{base}</sub>
                </span>
                <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 
                                text-xs text-gray-500 dark:text-gray-400 opacity-0 
                                group-hover:opacity-100 transition-opacity">
                  {baseLabel}
                </div>
              </div>
            </div>
          </>
        )}
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{octalValue}</span> (Octal) = <span className="text-green-500">{convertedValue}</span> ({baseLabel})
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText, getIntermediateConversionExplanation, showIntermediateConversion, showDetailedStep2, result, toggleIntermediateConversion, toggleDetailedStep2]);

   const getOctalToBase64Explanation = useCallback((octalValue, isExample, exampleResult) => {
     const decimalValue = parseInt(octalValue, 8);
     let base64Value;
     
     if (isEncodingMode) {
       base64Value = specialConversions['Base-64'].encode(decimalValue.toString());
     } else {
       base64Value = specialConversions['Base-64'].fromDecimal(decimalValue);
     }

    console.log('Octal:', octalValue);
    console.log('Decimal:', decimalValue);
    console.log('Base64:', base64Value);
    console.log('Is Example:', isExample);
    console.log('Example Result:', exampleResult);

    const CompactExplanation = () => (
      <div className="mt-4 p-6 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900 dark:to-green-900 rounded-lg shadow-md">
        <p className="text-center font-bold text-2xl mb-4">
          <span className="text-blue-600 dark:text-blue-400">{octalValue}</span><sub className="text-black dark:text-white">8</sub> →
          <span className="text-purple-600 dark:text-purple-400">{decimalValue}</span><sub className="text-black dark:text-white">10</sub> →
          <span className="text-green-600 dark:text-green-400">{base64Value}</span><sub className="text-black dark:text-white">64</sub>
        </p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="font-semibold text-blue-600 dark:text-blue-400">Octal</p>
            <p className="text-lg">{octalValue}</p>
          </div>
          <div>
            <p className="font-semibold text-purple-600 dark:text-purple-400">Decimal</p>
            <p className="text-lg">{decimalValue}</p>
          </div>
          <div>
            <p className="font-semibold text-green-600 dark:text-green-400">Base-64</p>
            <p className="text-lg">{base64Value}</p>
          </div>
        </div>
      </div>
    );

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Octal to Base-64 Conversion Works</h3>
        {renderExampleOrConversionText(octalValue, isExample)}
        <p className="mb-4">
          To convert an octal number to Base-64, we first convert the octal number to decimal, and then we convert the decimal to Base-64.
        </p>
        <ConversionFlow
          steps={['Octal', 'Decimal', 'Base-64']}
          darkMode={darkMode}
        />
        <ol className="list-decimal list-inside mb-4">
          <li className="mb-2">
            Convert octal to decimal: {octalValue}<sub>8</sub> = {decimalValue}<sub>10</sub>
            <button
              onClick={toggleIntermediateConversion}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showIntermediateConversion ? 'Hide Details' : 'Show Details'}
            </button>
          </li>
          {getIntermediateConversionExplanation(octalValue)}
          <li className="mb-2">
            Now, we convert the decimal number <span className="font-bold text-blue-500">{decimalValue}</span> to Base-64:
            <button
              onClick={toggleDetailedStep2}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showDetailedStep2 ? 'Hide Details' : 'Show Details'}
            </button>
            {showDetailedStep2 ? (
              <>
                <p className="mt-2 text-center font-semibold">
                  <span className="text-blue-500">{octalValue}</span><sub>8</sub> →
                  <span className="text-purple-500">{decimalValue}</span><sub>10</sub> →
                  <span className="text-green-500">...</span><sub>64</sub>
                </p>
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-4 rounded-xl overflow-hidden
                             border border-gray-200/80 dark:border-gray-700/80
                             bg-gradient-to-br from-gray-50 to-white
                             dark:from-gray-800/50 dark:to-gray-800
                             shadow-sm backdrop-blur-sm"
                  >
                    <div className="p-6">
                      <ConversionExplanationDecimalToAll
                        conversionType="number"
                        fromUnit="Decimal"
                        toUnit="Base-64"
                        inputValue={decimalValue.toString()}
                        result={base64Value}
                        darkMode={darkMode}
                        isEncodingMode={isEncodingMode}  // Added
                        onEncodingModeToggle={onEncodingModeToggle}  // Added
                      />
                    </div>
                  </motion.div>
                </AnimatePresence>
              </>
            ) : (
              <CompactExplanation />
            )}
          </li>
        </ol>
        {showDetailedStep2 && (
          <>
            <p className="mt-4 text-center font-semibold text-lg">
              Complete conversion flow:
            </p>
            <p className="mt-2 text-center">
              <span className="font-bold text-blue-500">{octalValue}<sub className="text-black">8</sub></span> →
              <span className="font-bold text-purple-500">{decimalValue}<sub className="text-black">10</sub></span> →
              <span className="font-bold text-green-500">{base64Value}<sub className="text-black">64</sub></span>
            </p>
          </>
        )}
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{octalValue}</span> (Octal) = <span className="text-green-500">{base64Value}</span> (Base-64)
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText, getIntermediateConversionExplanation, showIntermediateConversion, showDetailedStep2, result, toggleIntermediateConversion, toggleDetailedStep2, specialConversions, isEncodingMode, onEncodingModeToggle]);

  const convertToRoman = useCallback((num) => {
    const romanNumerals = [
      { value: 1000, numeral: 'M' },
      { value: 900, numeral: 'CM' },
      { value: 500, numeral: 'D' },
      { value: 400, numeral: 'CD' },
      { value: 100, numeral: 'C' },
      { value: 90, numeral: 'XC' },
      { value: 50, numeral: 'L' },
      { value: 40, numeral: 'XL' },
      { value: 10, numeral: 'X' },
      { value: 9, numeral: 'IX' },
      { value: 5, numeral: 'V' },
      { value: 4, numeral: 'IV' },
      { value: 1, numeral: 'I' }
    ];

    let result = '';
    for (let i = 0; i < romanNumerals.length; i++) {
      while (num >= romanNumerals[i].value) {
        result += romanNumerals[i].numeral;
        num -= romanNumerals[i].value;
      }
    }
    return result;
  }, []);

  const getExampleResult = useCallback((octalValue, toUnit) => {
    const decimalValue = parseInt(octalValue, 8);
    switch (toUnit) {
      case 'Decimal':
        return decimalValue.toString();
      case 'Binary':
        return decimalValue.toString(2);
      case 'Hexadecimal':
        return decimalValue.toString(16).toUpperCase();
      case 'Base-36':
        return decimalValue.toString(36).toUpperCase();
      case 'Base-64':
        return specialConversions['Base-64'].fromDecimal(decimalValue);
      case 'Roman Numeral':
        return convertToRoman(decimalValue);
      default:
        return '';
    }
  }, [convertToRoman, specialConversions]);

  const getOctalToRomanExplanation = useCallback((octalValue, isExample, exampleResult) => {
    const decimalValue = parseInt(octalValue, 8);
    const romanValue = isExample ? exampleResult : result;

    const CompactExplanation = () => (
      <div className="mt-4 p-6 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900 dark:to-green-900 rounded-lg shadow-md">
        <p className="text-center font-bold text-2xl mb-4">
          <span className="text-blue-600 dark:text-blue-400">{octalValue}</span><sub className="text-black dark:text-white">8</sub> →
          <span className="text-purple-600 dark:text-purple-400">{decimalValue}</span><sub className="text-black dark:text-white">10</sub> →
          <span className="text-green-600 dark:text-green-400">{romanValue}</span>
        </p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="font-semibold text-blue-600 dark:text-blue-400">Octal</p>
            <p className="text-lg">{octalValue}</p>
          </div>
          <div>
            <p className="font-semibold text-purple-600 dark:text-purple-400">Decimal</p>
            <p className="text-lg">{decimalValue}</p>
          </div>
          <div>
            <p className="font-semibold text-green-600 dark:text-green-400">Roman Numeral</p>
            <p className="text-lg">{romanValue}</p>
          </div>
        </div>
        <p className="mt-4 text-center text-gray-600 dark:text-gray-300 italic">
          Click "Show Details" for step-by-step explanation
        </p>
      </div>
    );

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Octal to Roman Numeral Conversion Works</h3>
        {renderExampleOrConversionText(octalValue, isExample)}
        <p className="mb-4">
          To convert an octal number to a Roman numeral, we first convert the octal number to decimal, and then we convert the decimal to a Roman numeral.
        </p>
        <ConversionFlow
          steps={['Octal', 'Decimal', 'Roman Numeral']}
          darkMode={darkMode}
        />
        <ol className="list-decimal list-inside mb-4">
          <li className="mb-2">
            Convert octal to decimal: {octalValue}<sub>8</sub> = {decimalValue}<sub>10</sub>
            <button
              onClick={toggleIntermediateConversion}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showIntermediateConversion ? 'Hide Details' : 'Show Details'}
            </button>
          </li>
          {getIntermediateConversionExplanation(octalValue)}
          <li className="mb-2">
            Now, we convert the decimal number <span className="font-bold text-blue-500">{decimalValue}</span> to a Roman numeral:
            <button
              onClick={toggleDetailedStep2}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showDetailedStep2 ? 'Hide Details' : 'Show Details'}
            </button>
            {showDetailedStep2 ? (
              <>
                <p className="mt-2 text-center font-semibold">
                  <span className="text-blue-500">{octalValue}</span><sub className="text-black">8</sub> →
                  <span className="text-blue-500">{decimalValue}</span><sub className="text-black">10</sub> →
                  <span className="text-blue-500">...</span>
                </p>
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-4 rounded-xl overflow-hidden
                             border border-gray-200/80 dark:border-gray-700/80
                             bg-gradient-to-br from-gray-50 to-white
                             dark:from-gray-800/50 dark:to-gray-800
                             shadow-sm backdrop-blur-sm"
                  >
                    <div className="p-6">
                      <ConversionExplanationDecimalToAll
                        conversionType="number"
                        fromUnit="Decimal"
                        toUnit="Roman Numeral"
                        inputValue={decimalValue.toString()}
                        result={romanValue}
                        darkMode={darkMode}
                      />
                    </div>
                  </motion.div>
                </AnimatePresence>
              </>
            ) : (
              <CompactExplanation />
            )}
          </li>
        </ol>
        {showDetailedStep2 && (
          <>
            <p className="mt-4 text-center font-semibold text-lg">
              Complete conversion flow:
            </p>
            <p className="mt-2 text-center">
              <span className="font-bold text-blue-500">{octalValue}<sub className="text-black">8</sub></span> →
              <span className="font-bold text-blue-500">{decimalValue}<sub className="text-black">10</sub></span> →
              <span className="font-bold text-green-500">{romanValue}</span>
            </p>
          </>
        )}
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{octalValue}</span> (Octal) = <span className="text-green-500">{romanValue}</span> (Roman Numeral)
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText, getIntermediateConversionExplanation, showIntermediateConversion, showDetailedStep2, result, toggleIntermediateConversion, toggleDetailedStep2]);

  const getOctalToBinaryExplanation = useCallback((octalValue, isExample, exampleResult) => {
    const decimalValue = parseInt(octalValue, 8);
    const binaryValue = isExample ? exampleResult : result;

    const CompactExplanation = () => (
      <div className="mt-4 p-6 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900 dark:to-green-900 rounded-lg shadow-md">
        <p className="text-center font-bold text-2xl mb-4">
          <span className="text-blue-600 dark:text-blue-400">{octalValue}</span><sub className="text-black dark:text-white">8</sub> →
          <span className="text-purple-600 dark:text-purple-400">{decimalValue}</span><sub className="text-black dark:text-white">10</sub> →
          <span className="text-green-600 dark:text-green-400">{binaryValue}</span><sub className="text-black dark:text-white">2</sub>
        </p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="font-semibold text-blue-600 dark:text-blue-400">Octal</p>
            <p className="text-lg">{octalValue}</p>
          </div>
          <div>
            <p className="font-semibold text-purple-600 dark:text-purple-400">Decimal</p>
            <p className="text-lg">{decimalValue}</p>
          </div>
          <div>
            <p className="font-semibold text-green-600 dark:text-green-400">Binary</p>
            <p className="text-lg">{binaryValue}</p>
          </div>
        </div>
        <p className="mt-4 text-center text-gray-600 dark:text-gray-300 italic">
          Click "Show Details" for step-by-step explanation
        </p>
      </div>
    );

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Octal to Binary Conversion Works</h3>
        {renderExampleOrConversionText(octalValue, isExample)}
        <p className="mb-4">
          To convert an octal number to binary, we first convert the octal number to decimal, and then we convert the decimal to binary.
        </p>
        <ConversionFlow
          steps={['Octal', 'Decimal', 'Binary']}
          darkMode={darkMode}
        />
        <ol className="list-decimal list-inside mb-4">
          <li className="mb-2">
            Convert octal to decimal: {octalValue}<sub>8</sub> = {decimalValue}<sub>10</sub>
            <button
              onClick={toggleIntermediateConversion}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showIntermediateConversion ? 'Hide Details' : 'Show Details'}
            </button>
          </li>
          {getIntermediateConversionExplanation(octalValue)}
          <li className="mb-2">
            Now, we convert the decimal number <span className="font-bold text-blue-500">{decimalValue}</span> to binary:
            <button
              onClick={toggleDetailedStep2}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showDetailedStep2 ? 'Hide Details' : 'Show Details'}
            </button>
            {showDetailedStep2 ? (
              <>
                <p className="mt-2 text-center font-semibold">
                  <span className="text-blue-500">{octalValue}</span><sub className="text-black">8</sub> →
                  <span className="text-blue-500">{decimalValue}</span><sub className="text-black">10</sub> →
                  <span className="text-blue-500">...</span>
                  <sub className="text-black">2</sub>
                </p>
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-4 rounded-xl overflow-hidden
                             border border-gray-200/80 dark:border-gray-700/80
                             bg-gradient-to-br from-gray-50 to-white
                             dark:from-gray-800/50 dark:to-gray-800
                             shadow-sm backdrop-blur-sm"
                  >
                    <div className="p-6">
                      <ConversionExplanationDecimalToAll
                        conversionType="number"
                        fromUnit="Decimal"
                        toUnit="Binary"
                        inputValue={decimalValue.toString()}
                        result={binaryValue}
                        darkMode={darkMode}
                      />
                    </div>
                  </motion.div>
                </AnimatePresence>
              </>
            ) : (
              <CompactExplanation />
            )}
          </li>
        </ol>
        {showDetailedStep2 && (
          <>
            <p className="mt-4 text-center font-semibold text-lg">
              Complete conversion flow:
            </p>
            <p className="mt-2 text-center">
              <span className="font-bold text-blue-500">{octalValue}<sub className="text-black">8</sub></span> →
              <span className="font-bold text-blue-500">{decimalValue}<sub className="text-black">10</sub></span> →
              <span className="font-bold text-green-500">{binaryValue}<sub className="text-black">2</sub></span>
            </p>
          </>
        )}
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{octalValue}</span> (Octal) = <span className="text-green-500">{binaryValue}</span> (Binary)
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText, getIntermediateConversionExplanation, showIntermediateConversion, showDetailedStep2, result, toggleIntermediateConversion, toggleDetailedStep2]);

  const getOctalToScientificNotationExplanation = useCallback((octalValue, isExample, exampleResult) => {
    let decimalValue = parseInt(octalValue, 8);
    let scientificNotation = '';
    let adjustedCoefficient = '';
    let adjustedExponent = '';
    
    if (isExample) {
      // Use a valid octal number for the example if the input is invalid
      if (isNaN(decimalValue)) {
        octalValue = '52';  // This is octal for decimal 42
        decimalValue = 42;
      }
      scientificNotation = exampleResult || decimalValue.toExponential();
    } else {
      // For non-example cases, use the result if available
      if (result) {
        scientificNotation = result;
      } else if (!isNaN(decimalValue)) {
        scientificNotation = decimalValue.toExponential();
      }
    }
    
    if (scientificNotation && scientificNotation !== 'Invalid') {
      const splitResult = scientificNotation.split('e');
      if (splitResult.length === 2) {
        const [coefficient, exponent] = splitResult;
        adjustedCoefficient = parseFloat(coefficient).toFixed(4);
        adjustedExponent = parseInt(exponent, 10);
        scientificNotation = `${adjustedCoefficient}e${adjustedExponent >= 0 ? '+' : ''}${adjustedExponent}`;
      } else {
        scientificNotation = 'Invalid';
        adjustedCoefficient = 'N/A';
        adjustedExponent = 'N/A';
      }
    } else {
      scientificNotation = 'Invalid';
      adjustedCoefficient = 'N/A';
      adjustedExponent = 'N/A';
    }
    const quizNumber = Math.floor(Math.random() * 1000000);

    const CompactExplanation = () => (
      <div className="mt-4 p-6 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900 dark:to-green-900 rounded-lg shadow-md">
        <p className="text-center font-bold text-2xl mb-4">
          <span className="text-blue-600 dark:text-blue-400">{octalValue}</span><sub className="text-black dark:text-white">8</sub> →
          <span className="text-purple-600 dark:text-purple-400">{decimalValue}</span><sub className="text-black dark:text-white">10</sub> →
          <span className="text-green-600 dark:text-green-400">{scientificNotation}</span>
        </p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="font-semibold text-blue-600 dark:text-blue-400">Octal</p>
            <p className="text-lg">{octalValue}</p>
          </div>
          <div>
            <p className="font-semibold text-purple-600 dark:text-purple-400">Decimal</p>
            <p className="text-lg">{decimalValue}</p>
          </div>
          <div>
            <p className="font-semibold text-green-600 dark:text-green-400">Scientific Notation</p>
            <p className="text-lg">{scientificNotation}</p>
          </div>
        </div>
        <p className="mt-4 text-center text-gray-600 dark:text-gray-300 italic">
          Click "Show Details" for step-by-step explanation
        </p>
      </div>
    );

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Octal to Scientific Notation Conversion Works</h3>
        {renderExampleOrConversionText(octalValue, isExample)}
        {isNaN(decimalValue) ? (
          <p className="text-red-500">Invalid octal number. Please enter a valid octal number.</p>
        ) : (
          <>
            <p className="mb-4">
              To convert an octal number to scientific notation, we first convert the octal number to decimal, and then we convert the decimal to scientific notation.
            </p>
            <ConversionFlow
              steps={['Octal', 'Decimal', 'Scientific Notation']}
              darkMode={darkMode}
            />
            <ol className="list-decimal list-inside mb-4">
              <li className="mb-2">
                Convert octal to decimal: {octalValue}<sub>8</sub> = {decimalValue}<sub>10</sub>
                <button
                  onClick={toggleIntermediateConversion}
                  className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
                >
                  {showIntermediateConversion ? 'Hide Details' : 'Show Details'}
                </button>
              </li>
              {getIntermediateConversionExplanation(octalValue)}
              <li className="mb-2">
                Now, we convert the decimal number <span className="font-bold text-blue-500">{decimalValue}</span> to scientific notation:
                <button
                  onClick={toggleDetailedStep2}
                  className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
                >
                  {showDetailedStep2 ? 'Hide Details' : 'Show Details'}
                </button>
                {showDetailedStep2 ? (
                  <>
                    <p className="mt-2 text-center font-semibold">
                      <span className="text-blue-500">{octalValue}</span><sub className="text-black">8</sub> →
                      <span className="text-blue-500">{decimalValue}</span><sub className="text-black">10</sub> →
                      <span className="text-blue-500">...</span>
                    </p>
                    <AnimatePresence>
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-4 rounded-xl overflow-hidden
                                 border border-gray-200/80 dark:border-gray-700/80
                                 bg-gradient-to-br from-gray-50 to-white
                                 dark:from-gray-800/50 dark:to-gray-800
                                 shadow-sm backdrop-blur-sm"
                      >
                        <div className="p-6">
                          <ConversionExplanationDecimalToAll
                            conversionType="number"
                            fromUnit="Decimal"
                            toUnit="Scientific Notation"
                            inputValue={decimalValue.toString()}
                            result={scientificNotation}
                            darkMode={darkMode}
                          />
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </>
                ) : (
                  <CompactExplanation />
                )}
              </li>
            </ol>
            {showDetailedStep2 && (
              <>
                <p className="mt-4 text-center font-semibold text-lg">
                  Complete conversion flow:
                </p>
                <p className="mt-2 text-center">
                  <span className="font-bold text-blue-500">{octalValue}<sub>8</sub></span> →
                  <span className="font-bold text-blue-500">{decimalValue}<sub>10</sub></span> →
                  <span className="font-bold text-green-500">{scientificNotation}</span>
                </p>
              </>
            )}
            <p className="mt-4 font-bold">
              Therefore, <span className="text-blue-500">{octalValue}</span> (Octal) = <span className="text-green-500">{scientificNotation}</span> (Scientific Notation)
            </p>
            
            {/* **Scientific Notation Quiz Section** */}
            
          </>
        )}
      </div>
    );
  }, [
    darkMode,
    renderExampleOrConversionText,
    getIntermediateConversionExplanation,
    showIntermediateConversion,
    showDetailedStep2,
    toggleIntermediateConversion,
    toggleDetailedStep2,
  ]);
  
  const getExplanation = useMemo(() => {
    const octalValue = inputValue || '52';
    const isExample = !result;
    const exampleResult = isExample ? getExampleResult(octalValue, toUnit) : result;

    if (toUnit === 'Decimal') {
      return getOctalToDecimalExplanation(octalValue, isExample, exampleResult);
    } else if (toUnit === 'Binary') {
      return getOctalToBinaryExplanation(octalValue, isExample, exampleResult);
    } else if (toUnit === 'Hexadecimal') {
      return getOctalToOtherBaseExplanation(octalValue, isExample, 16, 'Hexadecimal', exampleResult);
    } else if (toUnit === 'Base-36') {
      return getOctalToOtherBaseExplanation(octalValue, isExample, 36, 'Base-36', exampleResult);
    } else if (toUnit === 'Base-64') {
      return getOctalToBase64Explanation(octalValue, isExample, exampleResult);
    } else if (toUnit === 'Roman Numeral') {
      return getOctalToRomanExplanation(octalValue, isExample, exampleResult);
    }  else if (conversionType === 'number' && fromUnit === 'Octal' && toUnit === 'Scientific Notation') {
    return getOctalToScientificNotationExplanation(inputValue, isExample, result);
  }  else if (conversionType === 'number' && fromUnit === 'Octal' && toUnit === 'Scientific Notation') {
    return getOctalToScientificNotationExplanation(inputValue, isExample, result);
  } 
  }, [inputValue, toUnit, result, getExampleResult, getOctalToDecimalExplanation, getOctalToBinaryExplanation, getOctalToOtherBaseExplanation, getOctalToBase64Explanation, getOctalToRomanExplanation]);

  useEffect(() => {
    if (showIntermediateConversion) {
      const timer = setTimeout(() => setIsConversionRendered(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsConversionRendered(false);
    }
  }, [showIntermediateConversion]);

  // Common button styles for all detail toggles
  const DetailToggleButton = ({ onClick, isOpen, children }) => (
    <button
      onClick={onClick}
      className={`
        inline-flex items-center gap-2 
        px-3 py-1.5 ml-3 text-sm font-medium
        rounded-lg transition-all duration-200
        bg-gradient-to-r from-blue-500/90 to-indigo-500/90 
        hover:from-blue-500 hover:to-indigo-500
        text-white/90 hover:text-white
        shadow-sm hover:shadow
        transform hover:-translate-y-0.5
        border border-white/10
        backdrop-blur-sm
      `}
    >
      {children}
      {isOpen ? (
        <FaAngleUp className="w-3.5 h-3.5 transform transition-transform" />
      ) : (
        <FaAngleDown className="w-3.5 h-3.5 transform transition-transform" />
      )}
    </button>
  );

  // Navigation buttons (Previous/Next)
  const NavigationButton = ({ onClick, disabled, direction, children }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        flex items-center gap-2 px-4 py-2
        rounded-lg transition-all duration-200
        ${disabled 
          ? 'bg-gray-400 cursor-not-allowed opacity-50' 
          : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600'}
        text-white shadow-sm hover:shadow
        transform hover:-translate-y-0.5
        disabled:transform-none disabled:hover:shadow-none
      `}
    >
      {direction === 'prev' && <FaChevronLeft className="w-4 h-4" />}
      {children}
      {direction === 'next' && <FaChevronRight className="w-4 h-4" />}
    </button>
  );

  // Quiz button
  const QuizButton = ({ onClick, isOpen }) => (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-2 px-4 py-2
        rounded-lg transition-all duration-200
        bg-gradient-to-r from-purple-500 to-pink-500 
        hover:from-purple-600 hover:to-pink-600
        text-white shadow-sm hover:shadow
        transform hover:-translate-y-0.5
        font-medium
      `}
    >
      {isOpen ? 'Hide Quiz' : 'Test Your Knowledge'}
      {isOpen ? (
        <FaAngleUp className="w-4 h-4" />
      ) : (
        <FaAngleDown className="w-4 h-4" />
      )}
    </button>
  );

  // Quiz submit button
  const QuizSubmitButton = ({ onClick }) => (
    <button
      onClick={onClick}
      className={`
        ml-2 px-4 py-2
        rounded-lg transition-all duration-200
        bg-gradient-to-r from-green-500 to-emerald-500 
        hover:from-green-600 hover:to-emerald-600
        text-white shadow-sm hover:shadow
        transform hover:-translate-y-0.5
        font-medium text-sm
      `}
    >
      Submit
    </button>
  );

  return (
    <div className={`p-6 ${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'} font-sans leading-relaxed`}>
      {getExplanation}
      <div className="mt-6">
        <button
          onClick={() => setShowQuiz(!showQuiz)}
          className={`px-4 py-2 ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
        >
          {showQuiz ? 'Hide Quiz' : 'Test Your Knowledge'}
        </button>
      </div>

      {showQuiz && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mt-4 p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`}
        >
          <h4 className="font-semibold">Quick Quiz:</h4>
          <p>Convert the octal number {inputValue || '52'} to {toUnit.toLowerCase()}:</p>
          <input
            type="text"
            value={quizAnswer}
            onChange={(e) => setQuizAnswer(e.target.value)}
            className={`mt-2 px-2 py-1 border ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'} rounded-md`}
          />
          <button
            onClick={handleQuizSubmit}
            className={`ml-2 px-4 py-1 ${darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white rounded-md transition-colors`}
          >
            Submit
          </button>
          {quizResult !== null && (
            <p className={`mt-2 text-lg font-bold ${quizResult ? 'text-green-500' : 'text-red-500'}`}>
              {quizResult ? 'Correct!' : `Try again! The correct answer is ${result}.`}
            </p>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default React.memo(ConversionExplanationOctalToAll);
